import type { ComponentProps } from 'react';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { Button, Modal } from '@hotjar/hotsauce';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import type { OwnedAccount } from '@hotjar/account';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { uniqBy } from 'lodash-es';
import { loadedModal, trackClick, closedModal } from '@hotjar/webapp/tracking/common';
import { PlanChangeList } from './PlanChangeList';
import { SummaryOfChangesTotals } from './SummaryOfChangeTotals';
import { MODAL_NAME } from '../types';
import { S } from '../styles';

type Props = ComponentProps<typeof PlanChangeList> &
  ComponentProps<typeof SummaryOfChangesTotals> & {
    onCancel(): void;
    onSubmit(): void;
    onPopoverClicked: (name: string) => void;
    account: OwnedAccount;
  };

export function SummaryOfChanges({
  onCancel,
  onSubmit,
  changeList,
  account,
  ...props
}: Props): JSX.Element {
  const { t: tSummaryOfChanges } = useTranslationWithPrefix('billing:summary-of-changes');
  useEffectOnceWithDependencies(
    () => {
      loadedModal(MODAL_NAME.planChange, extractAccountGroupingProps(account), {
        modal_site_count: uniqBy(changeList, ({ siteLink }) => siteLink).length,
      });
    },
    [account],
    () => account !== undefined,
  );

  function handleCancel() {
    closedModal(MODAL_NAME.planChange, extractAccountGroupingProps(account));
    onCancel();
  }

  function handleSubmit() {
    trackClick({
      name: 'Confirm change',
      uiPosition: `${MODAL_NAME.planChange} modal`,
      groupData: extractAccountGroupingProps(account),
    });
    onSubmit();
  }

  return (
    <Modal
      contentLabel={tSummaryOfChanges('modal-content-label')}
      size="l"
      isOpen
      onClose={handleCancel}
    >
      <Modal.Header title={tSummaryOfChanges('title')} />
      <S.ModalBody>
        <S.PlanChangeListContainer>
          <PlanChangeList changeList={changeList} />
        </S.PlanChangeListContainer>
        <SummaryOfChangesTotals {...props} />
      </S.ModalBody>
      <Modal.Footer>
        <Button kind="ghost" onClick={handleCancel}>
          {tSummaryOfChanges('back-to-edit-plans')}
        </Button>
        <Button type="submit" onClick={handleSubmit}>
          {tSummaryOfChanges('confirm-and-pay')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
